/**
 * Piezas de UI reutilizables para el lobby: paneles redondeados y botones
 * táctiles (≥44px de alto, criterio de accesibilidad del plan). Todo lo que
 * vive en `src/ui/**` puede usar Phaser libremente — la regla de "lógica
 * pura sin Phaser" aplica a `core/cards/meta`, no a la capa de presentación.
 */

import Phaser from 'phaser';
import { PALETTE } from './palette';

/** Dibuja un panel con esquinas redondeadas y borde sutil. */
export function drawPanel(
  scene: Phaser.Scene,
  x: number,
  y: number,
  width: number,
  height: number,
  fill: number = PALETTE.panel,
  border: number = PALETTE.border,
  radius = 14
): Phaser.GameObjects.Graphics {
  const g = scene.add.graphics({ x, y });
  g.fillStyle(fill, 1);
  g.fillRoundedRect(0, 0, width, height, radius);
  g.lineStyle(2, border, 1);
  g.strokeRoundedRect(1, 1, width - 2, height - 2, radius);
  return g;
}

export interface UiButtonOptions {
  fill?: number;
  fillHover?: number;
  fillDisabled?: number;
  textColor?: string;
  fontSize?: string;
  radius?: number;
  border?: number;
}

/**
 * Botón táctil genérico: fondo redondeado + texto centrado. Expone
 * `setEnabled` y `setText` para refrescar estado sin reconstruir la UI.
 */
export class UiButton extends Phaser.GameObjects.Container {
  private readonly bg: Phaser.GameObjects.Graphics;
  private readonly label: Phaser.GameObjects.Text;
  private readonly width_: number;
  private readonly height_: number;
  private readonly opts: Required<UiButtonOptions>;
  private enabled = true;

  constructor(
    scene: Phaser.Scene,
    x: number,
    y: number,
    width: number,
    height: number,
    text: string,
    onClick: () => void,
    options: UiButtonOptions = {}
  ) {
    super(scene, x, y);
    this.width_ = width;
    this.height_ = height;
    this.opts = {
      fill: options.fill ?? PALETTE.panel2,
      fillHover: options.fillHover ?? PALETTE.panelLight,
      fillDisabled: options.fillDisabled ?? PALETTE.locked,
      textColor: options.textColor ?? '#f4f0ff',
      fontSize: options.fontSize ?? '15px',
      radius: options.radius ?? Math.min(12, height / 2),
      border: options.border ?? PALETTE.border,
    };

    this.bg = scene.add.graphics();
    this.label = scene.add
      .text(0, 0, text, {
        fontFamily: 'monospace',
        fontSize: this.opts.fontSize,
        color: this.opts.textColor,
        fontStyle: 'bold',
        align: 'center',
      })
      .setOrigin(0.5);

    this.add([this.bg, this.label]);
    const hitH = Math.max(height, 44); // zona táctil mínima 44px
    this.setSize(width, hitH);
    this.redraw();

    // OJO (bug histórico): Phaser suma `displayOrigin` (= origin × setSize)
    // al punto local ANTES de evaluar el hitArea — el rect se define desde la
    // esquina superior-izquierda, no centrado. Un Rectangle(-w/2, -h/2, …)
    // aquí corre la zona clickeable medio botón hacia arriba-izquierda.
    this.setInteractive({ useHandCursor: true, hitArea: new Phaser.Geom.Rectangle(0, 0, width, hitH), hitAreaCallback: Phaser.Geom.Rectangle.Contains });
    this.on('pointerdown', () => {
      if (!this.enabled) return;
      onClick();
    });
    this.on('pointerover', () => this.redraw(true));
    this.on('pointerout', () => this.redraw());

    scene.add.existing(this);
  }

  private redraw(hover = false): void {
    const w = this.width_;
    const h = this.height_;
    const fill = !this.enabled ? this.opts.fillDisabled : hover ? this.opts.fillHover : this.opts.fill;
    this.bg.clear();
    this.bg.fillStyle(fill, 1);
    this.bg.fillRoundedRect(-w / 2, -h / 2, w, h, this.opts.radius);
    this.bg.lineStyle(2, this.opts.border, this.enabled ? 1 : 0.5);
    this.bg.strokeRoundedRect(-w / 2 + 1, -h / 2 + 1, w - 2, h - 2, this.opts.radius);
    this.label.setAlpha(this.enabled ? 1 : 0.55);
  }

  setEnabled(enabled: boolean): this {
    this.enabled = enabled;
    this.redraw();
    return this;
  }

  setText(text: string): this {
    this.label.setText(text);
    return this;
  }
}

/** Formato compacto para montos grandes en la UI (1200 -> "1.2k"). */
export function formatCompact(value: number): string {
  const abs = Math.abs(value);
  if (abs >= 1_000_000) return `${(value / 1_000_000).toFixed(1)}M`;
  if (abs >= 1_000) return `${(value / 1_000).toFixed(1)}k`;
  return `${Math.round(value)}`;
}
